import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikProps } from 'formik'
import debounce from 'lodash/debounce'
import * as Yup from 'yup'
import { filterGeoAddressByPostalCode } from 'src/helpers/AddressHelpers'

import {
  AdsSearchFormProps,
  AdsSearchFormValues,
} from '../../../forms/AdsSearchForm'
import {
  ClassifiedCategories,
  ClassifiedSpecialties,
} from '../../../rest/types/Classified'
import Icon, { Icons } from '../../../components/Icon'
import { ActionButtonVariant } from '../../../components/ActionButton/styled'
import { useServiceRequest } from '../../../hooks/useServiceRequest'
import { actions, selectors } from '../../../redux'
import { GeoAddress } from '../../../rest/types/GeoAddress'

export const useAdsSortingFiltersFormProps = ({
  categories,
  specialities,
  isOpen,
  isLoading,
  initialValues,
  onSubmit,
  submitButtonVariant,
}: {
  categories: ClassifiedCategories
  specialities: ClassifiedSpecialties
  isOpen?: boolean
  isLoading?: boolean
  initialValues?: AdsSearchFormProps['form']['formikForm']['initialValues']
  onSubmit: AdsSearchFormProps['form']['formikForm']['onSubmit']
  submitButtonVariant?: 'primary' | 'secondary'
}): AdsSearchFormProps => {
  const { t } = useTranslation()
  const formikRef = useRef<FormikProps<AdsSearchFormValues>>(null)

  const [suggestedAddresses, setSuggestedAddresses] = useState<
    GeoAddress[] | null
  >()

  const [addresses, getAddresses] = useServiceRequest(
    selectors.geo.getAddresses,
    actions.geo.getAddressesRequest,
    actions.geo.getAddressesReset
  )

  useEffect(() => {
    if (addresses.success) {
      const filteredAddresses = addresses.data?.filter((ad) =>
        filterGeoAddressByPostalCode(ad.postcode)
      )
      setSuggestedAddresses(filteredAddresses)
    }
  }, [addresses?.data, addresses.success])

  const handleLoadSuggestions = useCallback(
    (value: string) => {
      let updatedValue = value

      if (value && /^\d+$/.test(value) && value.length === 2) {
        if (value.startsWith('75')) {
          updatedValue = '75001'
        } else {
          updatedValue = value + '000'
        }
        getAddresses({ q: updatedValue, type: 'municipality' })
        formikRef?.current?.setFieldValue('localization', updatedValue)
      } else if (value && value.length >= 3) {
        getAddresses({ q: value, type: 'municipality' })
      }
    },
    [getAddresses]
  )

  const debouncedLoadSuggestions = useMemo(
    () => debounce(handleLoadSuggestions, 500),
    [handleLoadSuggestions]
  )

  return useMemo(() => {
    return {
      texts: {
        offerTypeTitle: t('form_search_ads_offer_type_label'),
        specialityTitle: t('form_search_ads_speciality_label'),
        localizationTitle: t('form_search_ads_localization_label'),
        radiusTitle: t('form_search_ads_radius_label'),
        termTitle: t('form_search_ads_term_label'),
        submitButton: t('form_search_ads_submit_button_label'),
      },
      form: {
        formikForm: {
          innerRef: formikRef,
          validationSchema: Yup.object().shape({
            offerType: Yup.array().min(
              1,
              t('form_search_ads_offer_type_error')
            ),
          }),
          initialValues: initialValues ?? {
            offerType: [],
            specialities: [],
            localization: '',
            radius: '2000',
            term: '',
          },
          onSubmit: (values, helpers) => {
            const updatedValues = {
              ...values,
            }
            onSubmit?.(updatedValues, helpers)
          },
        },
        fields: {
          offersType: {
            placeholder: t('form_search_ads_offer_type_placeholder'),
            isMulti: true,
            isSearchable: false,
            leftAdornment: <Icon icon={Icons.profil} width={24} height={24} />,
            options:
              categories?.map((category) => ({
                label: category?.name ?? '',
                value: category?.id ?? '',
              })) ?? [],
          },
          specialities: {
            placeholder: t('form_search_ads_speciality_placeholder'),
            isSearchable: false,
            isMulti: true,
            leftAdornment: (
              <Icon icon={Icons.stethoscopeSecondary} width={24} height={24} />
            ),
            options:
              specialities?.map((speciality) => ({
                label: speciality?.name ?? '',
                value: speciality?.id ?? '',
              })) ?? [],
          },
          localization: {
            placeholder: t('form_search_ads_localization_placeholder'),
            leftAdornment: (
              <Icon icon={Icons.markerMap} width={24} height={24} />
            ),
            suggestions: suggestedAddresses?.length
              ? suggestedAddresses?.map((address) => ({
                  label: address.label,
                  value: address.label,
                  params: address,
                }))
              : [],
            onlySuggestion: false,
            loadSuggestions: debouncedLoadSuggestions,
            onSubmit: (selectedValue: any) => {
              if (selectedValue?.params) {
                formikRef.current?.setFieldValue(
                  'lat',
                  selectedValue?.params?.lat
                )
                formikRef.current?.setFieldValue(
                  'lng',
                  selectedValue?.params?.long
                )
              }
            },
          },
          radius: {
            isSearchable: false,
            isMulti: false,
            placeholder: '',
            options: [
              {
                label: '2 km',
                value: '2000',
              },
              {
                label: '5 km',
                value: '5000',
              },
              {
                label: '10 km',
                value: '10000',
              },
              {
                label: '15 km',
                value: '15000',
              },
              {
                label: '20 km',
                value: '20000',
              },
            ],
          },
          term: {
            placeholder: t('form_search_ads_term_placeholder'),
            leftAdornment: <Icon icon={Icons.search} width={24} height={24} />,
          },
        },
        submitButtonVariant:
          submitButtonVariant === 'primary'
            ? ActionButtonVariant.tertiary
            : ActionButtonVariant.quaternary,
      },
      isOpen: !!isOpen,
      isLoading: !!isLoading,
      removeFilters: {
        onClick: () => {
          formikRef?.current?.setFieldValue('localization', '')
          formikRef?.current?.setFieldValue('offerType', [])
          formikRef?.current?.setFieldValue('specialities', [])
          formikRef?.current?.setFieldValue('term', [])
        },
        label: t('form_search_ads_remove_filters_button_label'),
        type: 'button',
      },
    }
  }, [
    t,
    initialValues,
    categories,
    specialities,
    suggestedAddresses,
    debouncedLoadSuggestions,
    submitButtonVariant,
    isOpen,
    isLoading,
    onSubmit,
  ])
}
